import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

import ThemeToggle from './ThemeToggle.jsx';
import { cx } from '../lib/format.js';

/**
 * Layout — the public storefront shell.
 *
 * Also owns two cross-page behaviours that belong in one place rather than in
 * every page: restoring scroll position on navigation, and exposing a skip link
 * so keyboard users can jump past the header.
 */

function Logo({ className }) {
  return (
    <span className={cx('inline-flex items-center gap-2', className)}>
      {/* bg-shell rather than bg-ink-950: the ink ramp inverts in dark mode, which
          would turn this tile white and wash out the gradient wordmark. */}
      <span className="grid size-8 place-items-center rounded-lg bg-shell text-sm font-bold">
        <span className="bg-gradient-to-br from-brand-400 to-growth-400 bg-clip-text text-transparent">1Fi</span>
      </span>
      <span className="text-lg font-bold tracking-tight text-ink-900">EMI Store</span>
    </span>
  );
}

/** Reset scroll to the top whenever the path changes. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function Header() {
  const navLink = ({ isActive }) =>
    cx(
      'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
      isActive ? 'bg-ink-100 text-ink-900' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900',
    );

  return (
    <header className="sticky top-0 z-40 border-b border-ink-200/70 bg-white/85 backdrop-blur-md">
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <Link to="/" className="shrink-0" aria-label="1Fi EMI Store — home">
          <Logo />
        </Link>

        <nav className="flex items-center gap-1" aria-label="Main">
          <NavLink to="/" end className={navLink}>
            Shop
          </NavLink>
          <NavLink to="/how-it-works" className={cx(navLink, 'hidden sm:block')}>
            How it works
          </NavLink>
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              cx(
                'rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
                isActive ? 'bg-brand-100 text-brand-800' : 'text-brand-700 hover:bg-brand-50',
              )
            }
          >
            Admin
          </NavLink>

          <span className="mx-1 h-6 w-px bg-ink-200" aria-hidden="true" />
          <ThemeToggle />
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-20 border-t border-ink-200/70 bg-white">
      <div className="container-page py-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-sm">
            <Logo />
            <p className="mt-3 text-sm leading-relaxed text-ink-600">
              Buy today by pledging your mutual funds as collateral. Your units stay invested, so you keep any
              market upside while you repay.
            </p>
          </div>

          <div className="text-sm">
            <h2 className="font-semibold text-ink-900">Explore</h2>
            <ul className="mt-3 space-y-2 text-ink-600">
              <li>
                <Link to="/" className="hover:text-brand-700">
                  All products
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="hover:text-brand-700">
                  How pledging works
                </Link>
              </li>
              <li>
                <Link to="/admin" className="hover:text-brand-700">
                  Admin dashboard
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Presenting illustrative projections without this qualifier would be
            misleading, so it is part of the layout rather than optional copy. */}
        <div className="mt-8 border-t border-ink-200/70 pt-6 text-xs leading-relaxed text-ink-500">
          <p>
            <strong className="font-semibold text-ink-700">Illustrative figures.</strong> EMI amounts are computed
            on a reducing-balance basis from the interest rate shown. Projected mutual fund values assume a fixed
            annual growth rate and are illustrations only — market returns are not guaranteed, and the value of
            pledged units can fall as well as rise. A loan against mutual funds is subject to lender eligibility
            criteria and loan-to-value limits.
          </p>
          <p className="mt-3">
            Built as a demonstration project. Product names and images are used for illustrative purposes.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="flex min-h-dvh flex-col">
      <ScrollToTop />

      <a
        href="#main"
        className="sr-only-focusable fixed left-4 top-4 z-50 rounded-lg bg-ink-950 px-4 py-2 text-sm font-semibold text-white"
      >
        Skip to content
      </a>

      <Header />

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}
